import * as React from 'react'
import {ReactElement} from 'react'
import {NatOwnershipTransfersRepo} from '../repository/NatOwnershipTransfersRepo'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import HolidaysRepository from '../../../../components/HolidaysRepository'
import {Nullable} from '../../../../app/TypeHelpers'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {PageTitle, PolicyCheckBoxStyled} from '../../applications/common/resident/NatResidentApplicationsCommon'
import NatResidentApplicationDetailConfirmItem
    from '../../applications/common/resident/NatResidentApplicationDetailConfirmItem'
import {FullWidthColumnFlexContainer, LeftRowFlexContainer} from '../../../../styledComponents/FlexComponents'
import {
    GuestLoginInfoSessionStorageKey,
    NatGuestLoginInfoSessionStorageHelper,
} from '../../../common/authentication/NatGuestLoginInfoSessionStorageHelper'
import {formatDate} from '../../../../formatters/formatDate'
import moment, {DurationInputArg1} from 'moment/moment'
import {convertToRoomNumberForResident} from '../../../common/util/Natutil'
import {NatOldMemberInfo} from '../repository/NatGetOwnershipTransferOldMember'
import {
    createGuestWithErrorHandlingPage,
    GuestWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createGuestWithErrorHandlingPage'
import {GuestApplyButton, GuestBackButton, GuestPlainLinkStyledBackButton} from '../common/NatOwnershipTransfersCommon'
import styled from 'styled-components'
import {Colors} from '../../../../styledComponents/Constants'
import {Location} from 'react-router-dom'


interface Props {
    condominiumCd: string
    ownershipTransfersRepo: NatOwnershipTransfersRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    holidaysRepo: HolidaysRepository
    location: Location
}

interface State {
    applyId: Nullable<string>
    applyInfo: Nullable<NatOldMemberInfo>
    ownershipTransferDate: string
    oldMemberPaymentChangeDate: Nullable<string>
    newMemberPaymentChangeDate: Nullable<string>
    isLoadCompleted: boolean
    isConfirmed: boolean
    nextButtonDisabled: boolean
}

class NatPaymentChangeDateOldMemberConfirmPage
    extends React.Component<Props & GuestWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & GuestWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            applyId: null,
            applyInfo: null,
            ownershipTransferDate: '',
            oldMemberPaymentChangeDate: '',
            newMemberPaymentChangeDate: '',
            isLoadCompleted: false,
            isConfirmed: false,
            nextButtonDisabled: true,
        }

        this.onChangeConfirmed = this.onChangeConfirmed.bind(this)
        this.onClickBackButton = this.onClickBackButton.bind(this)
        this.onClickApplyButton = this.onClickApplyButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            await this.getOwnershipTransferInfo()
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    async getOwnershipTransferInfo(): Promise<void> {
        const {condominiumCd, ownershipTransfersRepo, eventEmitter} = this.props
        // 組合員資格変更届提出状況取得(申請ID取得のため)
        const getSubmitStatusResult = await this.props.asyncCallFunction(
            ownershipTransfersRepo.getOwnershipTransfersSubmitStatus.bind(
                ownershipTransfersRepo, condominiumCd,
            ),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            })
        // 組合員変更届の取得
        if (getSubmitStatusResult?.details.applyId) {
            const apiResponse = await this.props.asyncCallFunction(
                ownershipTransfersRepo.getOwnershipTransferOldMember.bind(
                    ownershipTransfersRepo, condominiumCd, getSubmitStatusResult.details.applyId),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )

            if (apiResponse?.details) {
                this.setState({
                    applyId: getSubmitStatusResult.details.applyId,
                    applyInfo: apiResponse.details.oldMemberInfo,
                    ownershipTransferDate: apiResponse.details.applyInfo.ownershipTransferDate,
                })
            }
        }
    }

    applicantRoom(): string {
        const condominiumName = NatGuestLoginInfoSessionStorageHelper
            .getStringItemOrNull(GuestLoginInfoSessionStorageKey.SELECTED_CONDOMINIUM_NAME)
        const ridgeName = NatGuestLoginInfoSessionStorageHelper
            .getStringItemOrNull(GuestLoginInfoSessionStorageKey.SELECTED_RIDGE_NAME)
        const roomNumber = convertToRoomNumberForResident(
            NatGuestLoginInfoSessionStorageHelper
                .getStringItemOrNull(GuestLoginInfoSessionStorageKey.SELECTED_ROOM_NUMBER),
        )

        return condominiumName + '　' + ridgeName + ' ' + roomNumber
    }

    getPaymentChangeDateDisplayValue(date: string, addMonths: DurationInputArg1): string {
        const momentDate = moment(date).clone().add(addMonths, 'months')
        return momentDate.get('year') + '年' + (momentDate.get('month') + 1) + '月'
    }

    onClickBackButton(): void {
        this.props.navigate(
            `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/old-member/agreement`,
            {replace: true},
        )
    }

    onClickApplyButton(): void {
        const applyInfo = this.state.applyInfo
        const applyId = applyInfo ? this.state.applyId : ''
        const url = `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/old-member/input/${applyId}`
        this.props.navigate(
            url,
            {
                replace: true,
                state: {
                    ownershipTransferDate: this.state.ownershipTransferDate,
                    applyInfo: applyInfo ? this.state.applyInfo : undefined,
                },
            },
        )
    }

    onChangeConfirmed(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            isConfirmed: event.target.checked,
        }, () => {
            this.updateNextButtonDisabled()
        })
    }

    updateNextButtonDisabled(): void {
        const nextButtonDisabled = !this.state.isConfirmed
        this.setState({nextButtonDisabled: nextButtonDisabled})
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return <></>
        }

        const ownershipTransferDate = this.state.ownershipTransferDate ? formatDate(this.state.ownershipTransferDate) : ''
        const oldMemberPaymentChangeDate = this.getPaymentChangeDateDisplayValue(this.state.ownershipTransferDate, 1)
        const newMemberPaymentChangeDate = this.getPaymentChangeDateDisplayValue(this.state.ownershipTransferDate, 2)

        return (
            <>
                <GuestPlainLinkStyledBackButton onClick={this.onClickBackButton}>
                    戻る
                </GuestPlainLinkStyledBackButton>
                <BlockSpacer height={'15px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    支払変更期日の確認
                </PageTitle>
                <BlockSpacer height={'20px'}/>
                対象住戸、組合員変更年月日、管理費等の支払変更期日を確認してください。<br/>
                内容に誤りがある場合は、仲介業者に修正を依頼してください。<br/>
                <BlockSpacer height={'20px'}/>
                <NatResidentApplicationDetailConfirmItem
                    title={'対象住戸'}
                    items={[`${this.applicantRoom()}`]}
                />
                <BlockSpacer height={'5px'}/>
                <NatResidentApplicationDetailConfirmItem
                    title={'組合員変更年月日'}
                    items={[`${ownershipTransferDate}`]}
                />
                <BlockSpacer height={'5px'}/>
                <NatResidentApplicationDetailConfirmItem
                    title={'管理費等の支払変更期日'}
                    items={[`旧組合員：${oldMemberPaymentChangeDate}分まで支払い`,
                        `新組合員：${newMemberPaymentChangeDate}分から支払い`]}
                />
                <BlockSpacer height={'20px'}/>
                <FullWidthColumnFlexContainer>
                    <PolicyCheckBoxStyled>
                        <LeftRowFlexContainer>
                            <GuestInput
                                type="checkbox"
                                checked={this.state.isConfirmed}
                                onChange={this.onChangeConfirmed}
                            />
                            <BlockSpacer width={'10px'}/>
                            内容を確認しました
                        </LeftRowFlexContainer>
                    </PolicyCheckBoxStyled>
                </FullWidthColumnFlexContainer>
                <BlockSpacer height={'30px'}/>
                <GuestApplyButton
                    disabled={this.state.nextButtonDisabled}
                    onClick={this.onClickApplyButton}
                >
                    <FullWidthColumnFlexContainer>
                        <div>
                            次へ進む
                        </div>
                    </FullWidthColumnFlexContainer>
                </GuestApplyButton>
                <BlockSpacer height={'30px'}/>
                <GuestBackButton onClick={this.onClickBackButton}>戻る</GuestBackButton>
                <BlockSpacer height={'15px'}/>
            </>
        )
    }
}

export default createGuestWithErrorHandlingPage(NatPaymentChangeDateOldMemberConfirmPage)

const GuestInput = styled('input')`
  accent-color: ${Colors.GuestPrimary};
`
